import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import SeoBreadcrumbs from '@/components/SeoBreadcrumbs';
import { ArrowRight, CheckCircle, Clock, Upload, FileText, Building } from 'lucide-react';

const EstateAgentFloorPlans = () => {
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Estate Agent Floor Plans", path: "/estate-agent-floor-plans" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        pageId="estate-agent-floor-plans"
        defaultTitle="Estate Agent Floor Plans UK | House Plan Direct"
        defaultDescription="Professional floor plans for estate agents. Rightmove & Zoopla ready. 24-48 hour turnaround from £40. Increase listing engagement and attract more buyers with professional floor plans."
        aiSummary="House Plan Direct provides professional floor plans for estate agents across the UK. Our floor plans are formatted for Rightmove, Zoopla, OnTheMarket, and property brochures. Plans from £40 with 24-48 hour turnaround. Upload your sketch and receive a listing-ready floor plan."
        keywords="estate agent floor plans, property listing floor plans, estate agent floor plan service, floor plans for estate agents UK, rightmove floor plans, zoopla floor plans"
      />
      <SeoBreadcrumbs items={breadcrumbs} />
      <Header />
      
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <Building className="h-12 w-12 text-blue-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Estate Agent Floor Plans
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Professional floor plans for property listings. Formatted for Rightmove, Zoopla, and property brochures.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" /> From £40
            </span>
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <Clock className="h-4 w-4 text-blue-600 mr-2" /> 24-48 hour delivery
            </span>
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <FileText className="h-4 w-4 text-purple-600 mr-2" /> JPEG, PDF & DWG
            </span>
          </div>
          <Link to="/order">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
              Order Floor Plan <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <p className="text-sm text-gray-500 mt-4">
            Prefer to create it yourself? Use the free <Link to="/floor-plan-creator" className="text-blue-600 underline hover:text-blue-700" data-testid="estate-agent-link-floor-plan-creator">Floor Plan Creator</Link> to draw and present your own marketing plan, or <Link to="/measurement-tool" className="text-blue-600 underline hover:text-blue-700" data-testid="estate-agent-link-measurement-tool">measure an existing plan online</Link>. See our guide: <Link to="/guides/how-to-create-an-estate-agent-floor-plan" className="text-blue-600 underline hover:text-blue-700" data-testid="estate-agent-link-guide">How to create an estate agent floor plan</Link>.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Floor Plans That Sell Properties</h2>
          <p className="text-gray-600 mb-8">
            Properties with floor plans receive significantly more enquiries than those without. Our professional 
            floor plans help estate agents showcase properties effectively, giving buyers a clear understanding 
            of the layout before they visit.
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Why Floor Plans Matter</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Increase listing views by up to 30%</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Help buyers pre-qualify before viewings</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Professional presentation builds trust</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Required by many property portals</span>
                </li>
              </ul>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">What You Get</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">JPEG for Rightmove & Zoopla</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">PDF for property brochures</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">DWG for technical use</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Room dimensions included</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Simple Pricing</h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="p-4">
                <div className="text-3xl font-bold text-blue-600 mb-2">£40</div>
                <div className="text-gray-600">Up to 3 rooms</div>
              </div>
              <div className="p-4">
                <div className="text-3xl font-bold text-blue-600 mb-2">+£5</div>
                <div className="text-gray-600">Per additional room</div>
              </div>
              <div className="p-4">
                <div className="text-3xl font-bold text-blue-600 mb-2">24-48hrs</div>
                <div className="text-gray-600">Turnaround time</div>
              </div>
            </div>
            <div className="text-center mt-6">
              <Link to="/pricing">
                <Button variant="outline">View Full Pricing <ArrowRight className="ml-2 h-4 w-4" /></Button>
              </Link>
            </div>
          </div>

          {/* How to Order */}
          <h3 className="text-2xl font-bold text-gray-900 mb-6">How to Order</h3>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { step: "1", title: "Upload Sketch", desc: "Send us a sketch, photo, or old floor plan" },
              { step: "2", title: "We Draw", desc: "We create your professional floor plan" },
              { step: "3", title: "Receive Plan", desc: "Get your listing-ready floor plan" }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="bg-blue-600 text-white rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Ready to Order Your Floor Plan?</h3>
            <p className="text-blue-100 mb-6">Upload your sketch and receive a professional floor plan within 24-48 hours.</p>
            <Link to="/order">
              <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-gray-100">
                Order Now <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EstateAgentFloorPlans;
